import React, { useCallback, useEffect, useRef, useState } from 'react';
import axios from 'axios';
import ListaMensajes from './ListaMensajes';
import EntradaMensaje from './EntradaMensaje';
import ModalArchivo from './ModalArchivo';
import useChatSocket from './useChatSocket';
import './Chat.css';

const INTERVALO_POLLING_MS = 5000;

// Tope del mensaje. No lo impone la base (la columna es LONGTEXT); es para que
// el chat siga siendo legible y el historial no crezca sin control.
const MAXIMO_CARACTERES = 2000;

/**
 * Chat de una simulación (lado entidad) o de una cotización (lado proveedor).
 *
 * Props:
 *  - titulo:               texto de la cabecera (p. ej. consecutivo + nombre)
 *  - referencia:           { campo: 'idSimulacion' | 'idCotizacion', valor }
 *  - tipoUsuarioPropio:    1 entidad, 2 proveedor (para saber qué mensajes son "míos")
 *  - idUsuarioPropio:      id del usuario logueado
 *  - urlBase:              misConstantes.URL_CONSULTA
 *  - urlSocket:            misConstantes.URL_CHAT (opcional; sin él solo hay polling)
 *  - servlets:             { mensajes, insertar, archivo, reenviar }
 *  - claveCodigoUsuario:   clave de localStorage con el código del usuario. Cada front usa
 *                          una distinta: Entidad 'codigoUsuario' (por defecto), Proveedor
 *                          'simuladores.proveedores.codigoUsuario'
 *  - abiertoInicial:       si arranca desplegado (false por defecto)
 */
export default function ChatSimulacion({
  titulo,
  referencia,
  tipoUsuarioPropio,
  idUsuarioPropio,
  urlBase,
  urlSocket,
  servlets,
  claveCodigoUsuario = 'codigoUsuario',
  abiertoInicial = false,
}) {
  const [abierto, setAbierto] = useState(abiertoInicial);
  const [mensajes, setMensajes] = useState([]);
  const [reenviandoId, setReenviandoId] = useState(null);
  const [mostrarArchivo, setMostrarArchivo] = useState(false);
  const [enviandoArchivo, setEnviandoArchivo] = useState(false);
  const [respuestaArchivo, setRespuestaArchivo] = useState('');
  const [error, setError] = useState('');
  const cargando = useRef(false);

  const codigo = () => localStorage.getItem(claveCodigoUsuario);
  const base = () => ({ codigo: codigo(), [referencia.campo]: referencia.valor });
  const url = (nombre) => urlBase.replace(/\/+$/, '') + '/' + nombre;

  // Los servlets leen los datos con request.getParameter, que Tomcat solo
  // alimenta desde la query string o desde un cuerpo de formulario. Se envían
  // en el cuerpo (no en la URL) porque la línea de petición y las cabeceras
  // están limitadas a 8 KB: un mensaje largo, o con tildes, hacía que Tomcat
  // rechazara la petición con 400 y el mensaje se perdía.
  const enviarFormulario = (nombreServlet, datos) => {
    const cuerpo = new URLSearchParams();
    Object.keys(datos).forEach((clave) => cuerpo.append(clave, datos[clave]));
    return axios.post(url(nombreServlet), cuerpo);
  };

  const cargar = useCallback(() => {
    if (cargando.current) {
      return Promise.resolve();
    }
    cargando.current = true;
    const info = base();
    return enviarFormulario(servlets.mensajes, info)
      .then((r) => {
        setMensajes(Array.isArray(r.data) ? r.data : []);
        setError('');
      })
      .catch(() => setError('No se pudieron cargar los mensajes'))
      .finally(() => {
        cargando.current = false;
      });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [referencia.campo, referencia.valor, urlBase, servlets.mensajes]);

  // Carga inicial + polling de respaldo (el mecanismo que ya usaba el monolito).
  useEffect(() => {
    cargar();
    const timer = setInterval(cargar, INTERVALO_POLLING_MS);
    return () => clearInterval(timer);
  }, [cargar]);

  const conexion = useChatSocket({ url: urlSocket, referencia, tipoUsuario: tipoUsuarioPropio, onMensaje: cargar, claveCodigoUsuario });

  const esPropio = (m) => m.tipoUsuario === tipoUsuarioPropio && String(m.idUsuario) === String(idUsuarioPropio);

  const enviar = (texto) => {
    const info = { ...base(), mensaje: texto };
    return enviarFormulario(servlets.insertar, info)
      .then(() => cargar())
      .catch(() => setError('No se pudo enviar el mensaje'));
  };

  const enviarArchivo = (archivo, texto) => {
    setEnviandoArchivo(true);
    setRespuestaArchivo('');
    const formData = new FormData();
    formData.append('archivo', archivo);
    formData.append('codigo', codigo());
    formData.append('mensaje', texto || '');
    formData.append(referencia.campo, referencia.valor);
    formData.append('idTipoArchivo', -1);
    formData.append('url', urlBase);
    return axios
      .post(url(servlets.archivo), formData, { headers: { 'Content-Type': 'multipart/form-data' } })
      .then(() => {
        setMostrarArchivo(false);
        return cargar();
      })
      .catch(() => setRespuestaArchivo('No se pudo subir el archivo'))
      .finally(() => setEnviandoArchivo(false));
  };

  const reenviar = (identificador) => {
    if (reenviandoId) {
      return Promise.resolve();
    }
    setReenviandoId(identificador);
    const info = { ...base(), identificadorMensaje: identificador };
    return enviarFormulario(servlets.reenviar, info)
      .catch(() => setError('No se pudo reenviar el correo'))
      .then(() => cargar())
      .finally(() => setReenviandoId(null));
  };

  const textoConexion = { conectado: 'Conectado', reconectando: 'Reconectando…', 'sin-socket': '' }[conexion] || '';

  return (
    <>
      <button
        type="button"
        className={'chat-flotante' + (abierto ? ' chat-flotante-oculto' : '')}
        onClick={() => setAbierto(true)}
        aria-label="Abrir chat"
        aria-hidden={abierto}
        tabIndex={abierto ? -1 : 0}
      >
        <i className="far fa-comment-alt" aria-hidden="true"></i>
      </button>
      <section
        className={'chat-panel' + (abierto ? ' chat-abierto' : ' chat-cerrado')}
        aria-label="Chat de la simulación"
        aria-hidden={!abierto}
      >
        <header className="chat-cabecera">
          <span className={'chat-conexion-punto chat-conexion-' + conexion} title={textoConexion || 'Conectado'}></span>
          <div className="chat-titulo" title={titulo}>
            {titulo}
          </div>
          <button type="button" className="chat-cerrar" onClick={() => setAbierto(false)} aria-label="Cerrar chat">
            <i className="fas fa-times" aria-hidden="true"></i>
          </button>
        </header>
        {error && <div className="chat-error">{error}</div>}
        <ListaMensajes mensajes={mensajes} esPropio={esPropio} reenviandoId={reenviandoId} onReenviar={reenviar} />
        <EntradaMensaje onEnviar={enviar} onAdjuntar={() => setMostrarArchivo(true)} maximoCaracteres={MAXIMO_CARACTERES} />
      </section>
      <ModalArchivo
        mostrar={mostrarArchivo}
        onCerrar={() => setMostrarArchivo(false)}
        onEnviar={enviarArchivo}
        enviando={enviandoArchivo}
        respuesta={respuestaArchivo}
      />
    </>
  );
}
